import type { Labelled } from './common.js';

/**
 * حد ائتمان العميل.
 *
 * البيع بالآجل قرضٌ يُمنح من سيولة الشركة: يُنفَّذ العمل وتُدفع مرتباته
 * ومصروفاته، ثم يُنتظر التحصيل شهرًا أو أكثر. وبلا سقف يتراكم الدَّين
 * تدريجيًا بلا لحظة يتوقف فيها أحد ليسأل عن مجموعه — كل فاتورة وحدها
 * معقولة، والمجموع هو ما يؤلم.
 *
 * والقياس على الفواتير المُرسَلة لا المسودات: المسودة تُعدَّل وتُحذف ولم
 * تصر التزامًا على أحد، والإرسال هو لحظة الإثبات المحاسبي في هذا النظام.
 */

/* ==========================================================================
   درجة الإلزام
   ========================================================================== */

export const CREDIT_ENFORCEMENTS = ['OFF', 'WARN', 'BLOCK'] as const;
export type CreditEnforcement = (typeof CREDIT_ENFORCEMENTS)[number];

export interface CreditEnforcementDef extends Labelled<CreditEnforcement> {
  description: string;
}

export const CREDIT_ENFORCEMENT_DEFS: readonly CreditEnforcementDef[] = [
  {
    key: 'OFF',
    label: 'معطّل',
    kind: 'neutral',
    description: 'تُعرض الأرصدة للاطلاع ولا يعترض النظام على شيء.',
  },
  {
    key: 'WARN',
    label: 'تحذير',
    kind: 'warning',
    description:
      'يُنبَّه من يتجاوز الحد ويمضي في عمله. يكشف المشكلة دون أن يوقف فريق البيع.',
  },
  {
    key: 'BLOCK',
    label: 'منع',
    kind: 'danger',
    description:
      'يُمنع تجاوز الحد إلا بتخويل صريح يُسجَّل باسم صاحبه وسببه. أأمن ماليًا وأبطأ عملًا.',
  },
] as const;

export const CREDIT_ENFORCEMENT_MAP = Object.fromEntries(
  CREDIT_ENFORCEMENT_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CreditEnforcement, CreditEnforcementDef>>;

/* ==========================================================================
   حالة العميل
   ========================================================================== */

export const CREDIT_STATUSES = ['OK', 'NEAR', 'OVER', 'CASH_ONLY', 'HOLD', 'UNLIMITED'] as const;
export type CreditStatus = (typeof CREDIT_STATUSES)[number];

export interface CreditStatusDef extends Labelled<CreditStatus> {
  description: string;
}

export const CREDIT_STATUS_DEFS: readonly CreditStatusDef[] = [
  { key: 'OK', label: 'ضمن الحد', kind: 'success', description: 'المستحق دون السقف بمسافة مريحة.' },
  {
    key: 'NEAR',
    label: 'قارب الحد',
    kind: 'warning',
    description: 'بلغ المستحق ثمانين بالمئة من السقف فأكثر.',
  },
  { key: 'OVER', label: 'تجاوز الحد', kind: 'danger', description: 'المستحق فوق السقف.' },
  {
    key: 'CASH_ONLY',
    label: 'نقدي فقط',
    kind: 'info',
    description: 'حدُّه صفر: لا يُباع له آجلًا، والسداد مقدَّم.',
  },
  {
    key: 'HOLD',
    label: 'موقوف',
    kind: 'danger',
    description: 'أُوقف تعامله الآجل يدويًا بصرف النظر عن أرقامه.',
  },
  {
    key: 'UNLIMITED',
    label: 'بلا حد',
    kind: 'neutral',
    description: 'لم يُحدَّد له سقف ولا سقف افتراضي للشركة.',
  },
] as const;

export const CREDIT_STATUS_MAP = Object.fromEntries(
  CREDIT_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<CreditStatus, CreditStatusDef>>;

/** النسبة التي يُعدّ العميل عندها مقاربًا لسقفه. */
export const CREDIT_NEAR_RATIO = 0.8;

/* ==========================================================================
   المستندات الخاضعة
   ========================================================================== */

export const CREDIT_DOCUMENTS = ['INVOICE', 'CONTRACT', 'QUOTATION'] as const;
export type CreditDocument = (typeof CREDIT_DOCUMENTS)[number];

export const CREDIT_DOCUMENT_LABELS: Readonly<Record<CreditDocument, string>> = {
  INVOICE: 'فاتورة',
  CONTRACT: 'عقد',
  QUOTATION: 'عرض سعر',
};

/* ==========================================================================
   الاحتساب
   ========================================================================== */

export interface CreditInput {
  /**
   * سقف العميل الخاص.
   *
   * ثلاث حالات متمايزة: null يعني «خذ افتراضي الشركة»، والصفر يعني «نقدي
   * فقط» صراحةً، وما فوقه هو السقف. الخلط بين الغياب والصفر يجعل من لم
   * يُضبط له سقف ممنوعًا من الشراء.
   */
  limit: number | null;
  /** سقف الشركة لمن لا سقف له. صفر يعني ألّا سقف افتراضي. */
  defaultLimit: number;
  /** المستحق الحالي: المُرسَل غير المحصَّل. */
  outstanding: number;
  /** إيقاف يدوي يعلو على الأرقام. */
  onHold: boolean;
}

export interface CreditState {
  status: CreditStatus;
  /** السقف السارِي فعلًا بعد تطبيق الافتراضي، أو null بلا سقف. */
  effectiveLimit: number | null;
  outstanding: number;
  /** ما بقي من السقف، أو null بلا سقف. سالبه يعني تجاوزًا. */
  available: number | null;
  /** نسبة الاستهلاك المئوية، أو null بلا سقف. */
  usedPercent: number | null;
}

function money(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * حالة عميل واحد.
 *
 * دالة خالصة بلا قاعدة بيانات: هي القاعدة التي يُمنع بها بيع، فتُختبر
 * وحدها بأرقام صريحة.
 */
export function creditState(input: CreditInput): CreditState {
  const outstanding = money(Math.max(0, input.outstanding));

  if (input.onHold) {
    return {
      status: 'HOLD',
      effectiveLimit: input.limit,
      outstanding,
      available: 0,
      usedPercent: null,
    };
  }

  /* الصفر الصريح على العميل: نقدي فقط، ولا يرث افتراضي الشركة. */
  if (input.limit === 0) {
    return {
      status: 'CASH_ONLY',
      effectiveLimit: 0,
      outstanding,
      available: 0,
      usedPercent: null,
    };
  }

  const effective = input.limit ?? (input.defaultLimit > 0 ? input.defaultLimit : null);

  if (effective === null) {
    return {
      status: 'UNLIMITED',
      effectiveLimit: null,
      outstanding,
      available: null,
      usedPercent: null,
    };
  }

  const available = money(effective - outstanding);
  const usedPercent = money((outstanding / effective) * 100);

  return {
    status:
      outstanding > effective ? 'OVER' : outstanding >= effective * CREDIT_NEAR_RATIO ? 'NEAR' : 'OK',
    effectiveLimit: effective,
    outstanding,
    available,
    usedPercent,
  };
}

export interface CreditDecision extends CreditState {
  /** المبلغ المطلوب إضافته الآن. */
  amount: number;
  /** المستحق بعد إتمام هذا المستند. */
  projected: number;
  /** هل يتجاوز هذا المستند السقف. */
  exceeds: boolean;
  /** المبلغ الزائد عن السقف، صفر إن لم يتجاوز. */
  excess: number;
  /** يمنع النظام إتمامه بلا تخويل. */
  blocked: boolean;
  /** رسالة تُعرض على المستخدم، أو null إن لا شيء يُقال. */
  message: string | null;
}

/**
 * قرار في مستند بعينه.
 *
 * الفصل بين exceeds وblocked مقصود: التجاوز واقعة رقمية تُعرض دائمًا،
 * والمنع قرار سياسة. تعطيل الإلزام يُسكت المنع ولا يُخفي الرقم.
 */
export function creditDecision(
  input: CreditInput & { amount: number; enforcement: CreditEnforcement },
): CreditDecision {
  const state = creditState(input);
  const amount = money(Math.max(0, input.amount));
  const projected = money(state.outstanding + amount);

  const base = { ...state, amount, projected };

  if (state.status === 'HOLD') {
    return {
      ...base,
      exceeds: true,
      excess: amount,
      blocked: input.enforcement === 'BLOCK',
      message: 'تعامل هذا العميل الآجل موقوف. راجع الإدارة قبل المضيّ.',
    };
  }

  if (state.effectiveLimit === null) {
    return { ...base, exceeds: false, excess: 0, blocked: false, message: null };
  }

  const excess = money(Math.max(0, projected - state.effectiveLimit));
  const exceeds = excess > 0;

  if (!exceeds) {
    /* التنبيه قبل التجاوز لا بعده: بلوغ الثمانين إشارة تسبق المشكلة. */
    const nearAfter = projected >= state.effectiveLimit * CREDIT_NEAR_RATIO;
    return {
      ...base,
      exceeds: false,
      excess: 0,
      blocked: false,
      message: nearAfter
        ? `يقترب هذا العميل من سقفه: يبلغ المستحق ${fmt(projected)} من ${fmt(state.effectiveLimit)}.`
        : null,
    };
  }

  const head =
    state.status === 'CASH_ONLY'
      ? 'هذا العميل نقدي فقط ولا يُباع له آجلًا'
      : `يتجاوز هذا المستند سقف العميل بمقدار ${fmt(excess)}`;

  const detail =
    state.status === 'CASH_ONLY'
      ? ''
      : ` — المستحق ${fmt(state.outstanding)} والسقف ${fmt(state.effectiveLimit)}.`;

  return {
    ...base,
    exceeds: true,
    excess,
    blocked: input.enforcement === 'BLOCK',
    message: `${head}${detail}`,
  };
}

/** رقم بفواصل الآلاف — للرسائل النصية القادمة من الخادم. */
function fmt(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
